import type {
  FileProcessingStatus,
  TaskStatus,
} from "@patent-studio/api-types";
export type { PipelineStepNode } from "@patent-studio/api-types/pipeline";
export {
  PIPELINE_STEP_TREE,
  PIPELINE_STEP_IDS,
  PIPELINE_STEP_LABELS,
  formatSectionLabel,
} from "@patent-studio/api-types/pipeline";

/** Accepted MIME types for document uploads (.pdf, .docx and .txt). */
export const DOCUMENT_ACCEPT: Record<string, string[]> = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "text/plain": [".txt"],
};

/**
 * The standing notice on the prior art library, held as content so it can be
 * reworded or removed without touching the screen once image and video prior
 * art is decided.
 */
export const PRIOR_ART_MEDIA_NOTICE =
  "This platform does not currently support prior art in the form of images or videos. These will need to be considered separately.";

/** Accepted MIME types for prior art uploads (.pdf). */
export const PRIOR_ART_ACCEPT: Record<string, string[]> = {
  "application/pdf": [".pdf"],
};

/** Technology domains for classification. */
export const DOMAINS = [
  { value: "software", label: "Software & Computing" },
  { value: "mechanical", label: "Mechanical Engineering" },
  { value: "electrical", label: "Electrical Engineering" },
  { value: "chemical", label: "Chemical & Materials" },
  { value: "biotech", label: "Biotechnology" },
  { value: "pharma", label: "Pharmaceutical" },
] as const;

/** Status styles keyed by task status (dot color + badge classes). */
export const STATUS_STYLES: Record<TaskStatus, { dot: string; badge: string }> =
  {
    created: {
      dot: "bg-muted-foreground",
      badge: "bg-muted text-muted-foreground",
    },
    ready: { dot: "bg-green-500", badge: "bg-green-50 text-green-700" },
    generating: {
      dot: "bg-primary animate-pulse",
      badge: "bg-primary/10 text-primary",
    },
    completed: { dot: "bg-green-500", badge: "bg-green-50 text-green-700" },
    failed: { dot: "bg-destructive", badge: "bg-red-50 text-red-700" },
  };

/** Badge variant mapping for file processing statuses. */
export const FILE_STATUS_VARIANT: Record<
  FileProcessingStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  indexed: "default",
  textract_complete: "default",
  textract_failed: "destructive",
  indexing_failed: "destructive",
  uploaded: "outline",
  textract_pending: "secondary",
  textract_processing: "secondary",
  indexing: "secondary",
  render_complete: "default",
  render_failed: "destructive",
  render_pending: "secondary",
  render_processing: "secondary",
  transcribe_complete: "default",
  transcribe_failed: "destructive",
  transcribe_pending: "secondary",
  transcribe_processing: "secondary",
  held: "outline",
};

/** File processing statuses that indicate active processing. */
export const ACTIVE_PROCESSING_STATUSES: ReadonlySet<FileProcessingStatus> =
  new Set([
    "textract_pending",
    "textract_processing",
    "indexing",
    "render_pending",
    "render_processing",
  ]);

/** File processing statuses that indicate a terminal state. */
export const TERMINAL_PROCESSING_STATUSES: ReadonlySet<FileProcessingStatus> =
  new Set([
    "indexed",
    "textract_failed",
    "indexing_failed",
    "render_complete",
    "render_failed",
    "transcribe_complete",
    "transcribe_failed",
  ]);

// ── DAG Stages ───────────────────────────────────────────────────────────

export { DAG_STAGE_TREE } from "@patent-studio/api-types/pipeline";

/**
 * The formats a figure may be.
 *
 * An image becomes a figure exactly as it is. A PDF is drawn on the server into
 * one image per page, and each page becomes a figure of its own, so the file the
 * attorney chooses is not the figure it produces. TIFF is neither: no part of
 * the tool can display it and nothing draws it.
 */
export const FIGURE_ACCEPT: Record<string, string[]> = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "application/pdf": [".pdf"],
};

/**
 * What each accepted format is called out loud.
 *
 * Derived from FIGURE_ACCEPT so the names cannot drift from the filter that
 * enforces them. One name per format, not one per extension: ".jpg" and
 * ".jpeg" are the same thing to an attorney reading a hint.
 */
export const FIGURE_ACCEPT_LABELS: readonly string[] = Object.values(
  FIGURE_ACCEPT,
).map((extensions) => formatLabel(extensions[0]));

/** ".pdf" as it is said: "PDF". */
function formatLabel(extension: string): string {
  return extension.replace(".", "").toUpperCase();
}

/**
 * What a stored document's format is called: from the extension the upload
 * table gives its type, or, for a type the table never took, the file's own.
 */
export function documentFormatLabel(
  contentType: string,
  filename: string,
): string {
  return formatLabel(
    contentType in DOCUMENT_ACCEPT
      ? DOCUMENT_ACCEPT[contentType][0]
      : `.${filename.split(".").pop() ?? "file"}`,
  );
}

/** What the figures drop zone tells the attorney it takes. */
export const FIGURE_ACCEPT_LABEL = FIGURE_ACCEPT_LABELS.join(" / ");
